import { useEffect, useState } from "react";

const WS_URL = process.env.REACT_APP_WS_URL;

/**
 * Opens a WebSocket connection to the team's real-time channel and calls
 * `onMessage` whenever the backend signals a `prs_changed` event. The
 * payload itself carries no PR/team data — it's purely an invalidation
 * signal, so `onMessage` is expected to be the same fetch callback the
 * caller already uses for its initial load (see Dashboard.js).
 *
 * Reconnects with exponential backoff (1s -> 30s cap) on close, and
 * immediately on tab refocus so a laptop-sleep/network-drop recovers fast
 * rather than waiting out the backoff. Re-reads the session token from
 * localStorage on every connection attempt (not just at mount) so a token
 * refreshed mid-session (e.g. after leaving/joining a team) is picked up.
 *
 * Returns a status string ("connected" | "reconnecting" | "offline" | "idle")
 * for callers that want to surface connection health in the UI.
 *
 * ⚠️ `enabled` is not an optimisation — it prevents a permanent reconnect
 * loop. ws-connect returns 403 for a user with no team (it has no team_id to
 * write into ws_connections), and the backoff below never gives up: it caps at
 * 30s and retries forever. A teamless user on the Dashboard therefore produced
 * a rejected Lambda invocation and a DB query every 30 seconds indefinitely, a
 * warn line per attempt into 30-day-retained logs, and — after three attempts —
 * an orange "● Offline" badge in the header, which is a false alarm for
 * someone whose only "problem" is not having a team yet.
 *
 * Disabled reports "idle" rather than "offline" so callers render no badge at
 * all. There is genuinely nothing to be live about with no team, and "offline"
 * would be claiming a fault that does not exist.
 */
export function useTeamSocket(onMessage, { enabled = true } = {}) {
  const [status, setStatus] = useState(enabled ? "connecting" : "idle");

  useEffect(() => {
    if (!WS_URL) return undefined;
    if (!enabled) {
      setStatus("idle");
      return undefined;
    }

    let socket = null;
    let reconnectDelay = 1000;
    let reconnectTimer = null;
    let failedAttempts = 0;
    let closedByUs = false;

    function connect() {
      const token = localStorage.getItem("session_token");
      if (!token) return;

      socket = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);

      socket.onopen = () => {
        reconnectDelay = 1000;
        failedAttempts = 0;
        setStatus("connected");
      };

      socket.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "prs_changed") onMessage();
        } catch {
          // Ignore malformed frames — the fallback poll covers any missed update.
        }
      };

      socket.onclose = () => {
        if (closedByUs) return;
        failedAttempts += 1;
        setStatus(failedAttempts >= 3 ? "offline" : "reconnecting");
        reconnectTimer = setTimeout(connect, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30000);
      };

      socket.onerror = () => {
        socket.close();
      };
    }

    connect();

    const onVisible = () => {
      if (document.visibilityState === "visible" && socket?.readyState !== WebSocket.OPEN) {
        clearTimeout(reconnectTimer);
        reconnectDelay = 1000;
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      closedByUs = true;
      clearTimeout(reconnectTimer);
      document.removeEventListener("visibilitychange", onVisible);
      socket?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMessage, enabled]);

  return status;
}
